// Permission names, mirroring member.permissions (seeded by
// db/flyway/static/sql/V10__permissions.sql).
//
// These strings appear literally in requirePermission() calls on the
// backend and in RequirePermission guards on the frontend, so they are
// code, not data -- adding one means a deploy and a matching static
// migration. What stays editable at runtime is who HOLDS them: which roles
// carry which permissions, and which individual members have been granted
// or denied one on top of their roles.
//
// Namespaced by domain, matching the schema layout: `member.*` is the club
// register, `flight.*` is the fleet and everything booked on it, `intra.*`
// is the intranet shell itself. The `ressu.*` names this replaced said
// which APP owned the permission, which stopped being true the moment
// aircraft moved to a schema fsm and webplan also read.
export const PERMISSIONS = {
  adminAccess: 'intra.admin.access',
  reportRead: 'intra.report.read',
  // Reading the club's events, members-only ones included, and signing up
  // for them: every membership role, but not an observer. Seeded by
  // db/flyway/static/sql/V310__event_read_permission.sql.
  eventRead: 'intra.event.read',
  // Adding, editing and deleting club events.
  eventWrite: 'intra.event.write',
  // Members' sign-in history, their live sessions and the failed-login
  // report (member.login_events). Admin only by default: it shows IPs and
  // locations. Seeded by db/flyway/static/sql/V70__login_audit_permission.sql.
  loginAuditRead: 'intra.audit.login.read',
  // The email delivery log, and resending / previewing / test-sending.
  // Admin only by default: the log shows every member's address and what
  // they were told. Seeded by db/flyway/static/sql/V90__email_permissions.sql.
  emailRead: 'intra.email.read',
  emailManage: 'intra.email.manage',
  // Viewing the Mailchimp sync status and triggering a manual sync. Admin
  // only by default. Seeded by db/flyway/static/sql/V110__mailchimp_permissions.sql.
  mailchimpManage: 'intra.mailchimp.manage',
  // The flight-log sync outbox monitor, and retrying a failed row. Admin
  // only by default. Seeded by db/flyway/static/sql/V200__flight_sync_permissions.sql.
  outboxRead: 'intra.outbox.read',
  outboxManage: 'intra.outbox.manage',
  // The external data the planner is built on (the AIP): the
  // admin Data sources page, a re-check, and uploading an AIP snapshot.
  // Admin only by default. Seeded by db/flyway/static/sql/V220__data_sources.sql.
  dataSourcesManage: 'intra.data_sources.manage',

  // The whole member roster: admins, the board and the heads of training. A plain member holds memberDirectoryRead
  // instead and sees only DIRECTORY_ROLES' holders. Seeded by
  // db/flyway/static/sql/V390__member_directory.sql.
  memberRead: 'member.read',
  memberDirectoryRead: 'member.directory.read',
  memberWrite: 'member.write',
  // Granting or denying one permission to one member, as opposed to
  // deciding who is a jäsensihteeri. Deliberately separate from memberWrite
  // so the club can keep those in different hands.
  permissionGrant: 'member.permission.grant',
  // Editing ANOTHER member's licences and medical. Everyone may edit their
  // own; a head of training may edit their department's students and
  // instructors through fsm*Manage below.
  licenceManage: 'member.licence.manage',
  // Reviewing applications from the public /join form, and the club rules
  // PDF it links to -- the member secretary's. Fee schedules are the
  // treasurer's. Both seeded by db/flyway/static/sql/V80__membership.sql.
  applicationManage: 'member.application.manage',
  feeManage: 'member.fee.manage',
  // Revealing a member's personal identity code and IBAN. Held by the
  // treasurer and the administrator; every reveal is logged
  // (member.sensitive_access_log) and needs a stated reason.
  sensitiveRead: 'member.sensitive.read',

  bookingRead: 'flight.booking.read',
  bookingCreate: 'flight.booking.create',
  bookingManageOwn: 'flight.booking.manage_own',
  bookingManageAny: 'flight.booking.manage_any',
  bookingBypassRules: 'flight.booking.bypass_rules',
  bookingBulk: 'flight.booking.bulk',

  // The palolento (fire-patrol) calendar. Read: see it and sign up for a
  // day -- carried by the fire_patrol roles, the committee and admin, and by
  // nobody else. Dispatch: choose the crew, name the week's dispatcher, keep
  // the crew list and book the aircraft for the season.
  // Seeded by db/flyway/static/sql/V300__fire_patrol.sql.
  firePatrolRead: 'flight.firepatrol.read',
  firePatrolDispatch: 'flight.firepatrol.dispatch',

  aircraftRead: 'flight.aircraft.read',
  aircraftWrite: 'flight.aircraft.write',

  maintenanceRead: 'flight.maintenance.read',
  maintenanceWrite: 'flight.maintenance.write',
  messageAck: 'flight.message.ack',
  // The defects module: anyone flying may report; triage publishes, holds
  // and resolves. Seeded by db/flyway/static/sql/V270__defect_permissions.sql.
  defectReport: 'flight.defect.report',
  defectTriage: 'flight.defect.triage',
  // The snag list: minor or long-term issues outside the official defects.
  // Seeded by db/flyway/static/sql/V280__snag_permission.sql.
  snagManage: 'flight.snag.manage',
  // Dents on the aircraft's 3D model or silhouette: upload the model and
  // manage the markers. Anyone signed in may look.
  // Seeded by db/flyway/static/sql/V290__dent_permission.sql.
  dentManage: 'flight.dent.manage',
  // The inventory: spare parts and other stock, their lots and locations.
  // Manage implies read. Seeded by db/flyway/static/sql/V320__inventory_permissions.sql.
  inventoryRead: 'flight.inventory.read',
  inventoryManage: 'flight.inventory.manage',

  // The club's general documents (club.documents): board minutes, rules,
  // forms, bulletins. documentRead sees the members' ones; documentReadBoard
  // the board-only ones (it is NOT implied by manage); manage uploads, edits,
  // tags, hides and deletes. Under `intra.` because the catalogue's `app`
  // check admits only the apps that exist. Seeded by
  // db/flyway/static/sql/V360__document_permissions.sql.
  documentRead: 'intra.document.read',
  documentReadBoard: 'intra.document.read_board',
  documentManage: 'intra.document.manage',

  // The club exams (exam.*): knowledge tests and onboarding checks. take sits
  // them and reads one's own results; admin sees everyone's attempts, signs
  // checklists and records a group; manage creates, imports, edits and
  // publishes exams. Seeded by db/flyway/static/sql/V410__exam_permissions.sql.
  examTake: 'intra.exam.take',
  examAdmin: 'intra.exam.admin',
  examManage: 'intra.exam.manage',

  // The expense claims (acct.expense_*): submit makes, signs and reads one's
  // own claims; process sees everyone's, decides them and marks them paid.
  // Seeded by db/flyway/static/sql/V430__expense_permissions.sql, where the
  // treasurer, the chairman and the expense_processor role get process.
  expenseSubmit: 'intra.expense.submit',
  expenseProcess: 'intra.expense.process',
  // A member's own invoices from the accounting system (Netvisor).
  invoiceRead: 'intra.invoice.read',

  // The flight log and the aircraft journey log books. Seeded by
  // db/flyway/static/sql/V130__flight_log_permissions.sql: every flying
  // role reads and logs its own flights; manage is the fleet manager's
  // (anyone's flights, opening books, choosing flight types).
  flightLogRead: 'flight.log.read',
  flightLogCreate: 'flight.log.create',
  flightLogManage: 'flight.log.manage',

  // The flight school, folded in from jupi-fsm-app. The department (ATO
  // under FI.ATO.2030, DTO under FI.DTO.0012) is part of the name, so
  // "an ATO instructor" is a permission check like any other -- see
  // db/flyway/static/sql/V10__permissions.sql.
  fsmRead: 'fsm.read',
  fsmAtoStudent: 'fsm.ato.student',
  fsmDtoStudent: 'fsm.dto.student',
  fsmAtoInstruct: 'fsm.ato.instruct',
  fsmDtoInstruct: 'fsm.dto.instruct',
  fsmAtoManage: 'fsm.ato.manage',
  fsmDtoManage: 'fsm.dto.manage',
  fsmManualGeneric: 'fsm.manual.generic',

  // The flight planner, folded in from webplan-modern. fplPlanUse covers a
  // pilot's own plans, routes, waypoints and private aircraft; ownership
  // keeps members out of each other's. fplAdmin manages the shared
  // waypoints. The fleet's performance and W&B data is aircraftWrite.
  fplPlanUse: 'fpl.plan.use',
  fplAdmin: 'fpl.admin',
} as const

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

// Role names seeded by db/flyway/static/sql/V20__roles.sql. These ARE the
// primary keys of member.roles -- the role is called 'instructor' in the
// database, in the JWT and here. Unlike permissions they are not exhaustive
// at compile time (the club can create more from the admin UI), so treat
// this as the set that ships, not the set that exists.
export const SEEDED_ROLES = [
  'member',
  'flying_member',
  'honorary_member',
  'instructor',
  'treasurer',
  'member_secretary',
  'committee',
  'admin',
  'ato_student',
  'dto_student',
  'ato_instructor',
  'dto_instructor',
  'ato_head_of_training',
  'ato_chief_flying_instructor',
  'dto_head_of_training',
  'fire_patrol',
  'fire_patrol_dispatcher',
] as const

// The flight school's two departments, and the permission that carries
// each department-scoped capability. Lets code say
// `SCHOOL_PERMISSIONS.ato.manage` instead of branching on a string.
export const SCHOOL_DEPARTMENTS = ['ato', 'dto'] as const
export type SchoolDepartment = (typeof SCHOOL_DEPARTMENTS)[number]

export const SCHOOL_PERMISSIONS = {
  ato: {
    student: PERMISSIONS.fsmAtoStudent,
    instruct: PERMISSIONS.fsmAtoInstruct,
    manage: PERMISSIONS.fsmAtoManage,
  },
  dto: {
    student: PERMISSIONS.fsmDtoStudent,
    instruct: PERMISSIONS.fsmDtoInstruct,
    manage: PERMISSIONS.fsmDtoManage,
  },
} as const satisfies Record<SchoolDepartment, Record<string, Permission>>

// Whose contact details every member may see in the club menu: the board,
// the club's officers and the instructors.
export const DIRECTORY_ROLES = [
  'committee',
  'treasurer',
  'member_secretary',
  'instructor',
  'ato_instructor',
  'dto_instructor',
  'ato_head_of_training',
  'ato_chief_flying_instructor',
  'dto_head_of_training',
] as const

export type SeededRole = (typeof SEEDED_ROLES)[number]

export const LOCALES = ['fi', 'sv', 'en'] as const
export type Locale = (typeof LOCALES)[number]

// flight.bookings.kind / .status
export const BOOKING_KINDS = ['reservation', 'option'] as const
// flight.bookings.booking_type: derived in the database from is_maintenance
// and the lesson columns. 'private' is every flight that is neither.
export const BOOKING_TYPES = ['private', 'training', 'maintenance'] as const
export type BookingType = (typeof BOOKING_TYPES)[number]
// The choices offered for dropping an unconfirmed standby, in hours before the
// booking starts (0 = at the start); also "never", which is no value.
export const STANDBY_DROP_HOURS = [0, 1, 2, 6, 12, 24, 48] as const
export const BOOKING_STATUSES = ['active', 'cancelled', 'promoted'] as const

// flight.aircraft_messages.message_class, carried over verbatim from
// ShowNotam.php.
export const MESSAGE_CLASSES = {
  hoursReport: 1, // tuntitilanneviesti
  general: 2, // yleisviesti
  defect: 3, // vikaviesti
  maintenance: 4, // huoltotapahtuma
} as const

// flight.aircraft_defects.status / .restriction. A report is 'pending' (seen
// only by its reporter and triage) until published to the public list or
// put on the hold list (does not ground the aircraft; fix at the next
// scheduled maintenance); 'resolved' closes it.
export const DEFECT_STATUSES = ['pending', 'published', 'held', 'resolved'] as const
export type DefectStatus = (typeof DEFECT_STATUSES)[number]
// How many days triage may ground an aircraft for in one go.
export const AOG_MIN_DAYS = 1
export const AOG_MAX_DAYS = 10
export const DEFECT_RESTRICTIONS = ['no_night', 'no_ifr', 'grounded'] as const
export type DefectRestriction = (typeof DEFECT_RESTRICTIONS)[number]

// member.user_permissions.effect. An individual grant adds a permission the
// member's roles do not carry; a deny withholds one they do, without
// stripping the role that carries it. Deny always wins -- see
// db/flyway/schema/sql/V90__effective_permissions.sql.
// The fuel the flight planner assumes for an aircraft's W&B density
// (flight.aircraft.fuel_type).
export const FUEL_TYPES = ['AVGAS', 'MOGAS', 'DIESEL'] as const
export type FuelType = (typeof FUEL_TYPES)[number]

export const PERMISSION_EFFECTS = ['grant', 'deny'] as const
export type PermissionEffect = (typeof PERMISSION_EFFECTS)[number]
